"use client";

import { useEffect } from "react";
import { gsap } from "gsap";
import { useGSAP } from "@gsap/react";

import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(useGSAP,ScrollTrigger);

interface UseLandingEffectsProps {
    containerRef: React.RefObject<HTMLElement | null>;
    leftLogoRef: React.RefObject<HTMLElement | null>;
    rightLogoRef: React.RefObject<HTMLElement | null>;
    active: boolean;
}
export default function useLandingEffects({
                                              containerRef,
                                              leftLogoRef,
                                              rightLogoRef,
                                              active,
                                          }: UseLandingEffectsProps) {
    useEffect(() => {
        const container = containerRef.current;
        const left = leftLogoRef.current;
        const right = rightLogoRef.current;

        if (!active || !container || !left || !right) return;

        const prefersReduced =
            typeof window !== "undefined" &&
            window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;
        const isCoarse =
            typeof window !== "undefined" &&
            window.matchMedia?.("(pointer: coarse)")?.matches;

        if (prefersReduced || isCoarse) return;

        let frame: number | null = null;
        let enabled = true;
        let centerX = 0;
        let centerY = 0;

        const measure = () => {
            const r = container.getBoundingClientRect();
            centerX = r.left + r.width / 2;
            centerY = r.top + r.height / 2;
        };
        measure();
        const ro = new ResizeObserver(() => {
            if (frame) cancelAnimationFrame(frame);
            frame = requestAnimationFrame(() => {
                measure();
                frame = null;
            });
        });
        ro.observe(container);
        const onResize = () => {
            if (frame) cancelAnimationFrame(frame);
            frame = requestAnimationFrame(() => {
                measure();
                frame = null;
            });
        };
        window.addEventListener("resize", onResize, { passive: true });
        const io = new IntersectionObserver(
            ([entry]) => {
                enabled = entry.isIntersecting;
            },
            { threshold: 0.15 }
        );
        io.observe(container);
        const apply = (dx: number, dy: number) => {
            left.style.transform = `translate3d(calc(-50% + ${-dx}px), calc(-50% + ${dy}px), 0)`;
            right.style.transform = `translate3d(calc(-50% + ${-dx}px), calc(-50% + ${dy}px), 0)`;
        };

        const onMove = (e: MouseEvent) => {
            if (window.scrollY !== 0 || !enabled || frame !== null) return;

            const { clientX, clientY } = e;
            frame = requestAnimationFrame(() => {
                const deltaX = (clientX - centerX) / 50;
                const deltaY = (clientY - centerY) / 50;
                apply(deltaX, deltaY);
                frame = null;
            });
        };

        window.addEventListener("mousemove", onMove, { passive: true });

        const cleanup = () => {
            window.removeEventListener("mousemove", onMove as any);
            window.removeEventListener("resize", onResize);
            io.disconnect();
            ro.disconnect();
            if (frame) cancelAnimationFrame(frame);
            left.style.transform = `translate3d(-50%, -50%, 0)`;
            right.style.transform = `translate3d(-50%, -50%, 0)`;
        };

        return cleanup;
    }, [containerRef, leftLogoRef, rightLogoRef, active]);
}